// Filter — a scouting tool that narrows the whole population by stacking bio-attribute
// chips with per-metric range sliders, ported from js/renderers-filter.js via the 04b bridge.
//   04g-4b (this): the CORE — season select, the bio chip-bar (the StatsFilterKit hybrid, as
//     Rankings), the population readout, and the paginated results list. The metric-range
//     slider pane is 04g-4c (added as the first child of .filter-body-layout, purely additive).
//   04g-4c: the ~162 metric-range sliders (imperative island) + the Per 90/Total toggle they
//     rescale.
//
// needsPlayer:false — a Filter isn't about one player (it builds its own sub-controls, like
// Rankings). Instance state (mod._filter*) is captured by main.js saveState/undo/presets.
//
// The bio chip-bar is the shared React <FilterChipBar> (04h-1): mod._filterBioFilters mutated
// in place; onChange bumps a version that hands a fresh clone to useFilterResults. (Before
// 04h-1 it was the legacy window.StatsFilterKit imperative island — now retired.) The
// metric-range sliders stay their own imperative island (filter-sliders.ts).
//
// ?live=1 (Track 2 demo push): the board (`useFilterBoard`) matches her league's Stats file
// instead of the snapshot — the canvas player's league (`_filterSourcePlayer`), its built seasons
// in the picker, the chips the files have data for. The widget's look is unchanged.

import { ModuleExplainer, shortNameOrPlayer } from "./ModuleExplainer.js";
import { explainFilter } from "./explain.js";
import { isLeagueCleared, useLeagueChip } from "../hooks/useLeagueChip.js";
import { useGenderSwitch } from "../hooks/useGenderSwitch.js";
import { womensOf } from "../live/gender-switch.js";
import { GenderSwitch } from "./GenderSwitch.js";
import { positionRowKey, whyTwice, type PositionRow } from "../live/group-players.js";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { ProtoFilterResult } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { PopulationReadout } from "./PopulationReadout.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { useFilterMetricByKey, useFilterBoard } from "../hooks/useFilterResults.js";
import { LIVE } from "../live/flag.js";
import { useMetricCategories } from "../hooks/useMetricCategories.js";
import { buildFilterSliders, type FilterSliderIsland } from "./filter-sliders.js";
import { hasTotalMode, type ActiveFilters } from "../engines/stats-filter-kit.js";
import type { FilterMetricRanges } from "../data/filter.js";
import { getStickyPlayer, setStickyPlayer } from "../canvas/sticky-player.js";
import { playerById, playerIdOf, type PlayerId } from "../data/players.js";
import { startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { noteRowPlayer } from "../live/player-gender.js";

const RESULTS_PAGE_SIZE = 25;

export function FilterModule({ mod, body }: ProtoModuleProps) {
  const metricMap = useFilterMetricByKey();
  const categories = useMetricCategories();
  // Only offer the Per 90/Total toggle if some metric has a Total mode (every category but
  // Appearance does — computed, not assumed, matching the legacy).
  const anyTotalMode = useMemo(
    () => categories.some((cat) => cat.subcategories.some((sub) => sub.playerMetrics.some(hasTotalMode))),
    [categories],
  );

  // The season picked; null = the default (the board resolves it: the snapshot's newest, or live
  // her current season). Saved to the `_`-state at once (T2-1 pattern: Retry retries the pick).
  const [seasonPick, setSeasonPick] = useState<string | null>(() => getModState<string>(mod, "_filterSeason") ?? null);
  const [mode, setMode] = useState<string>(() =>
    getModState<string>(mod, "_filterValueMode") === "total" ? "total" : "per90",
  );

  // The bio-filter chip state — the SAME mutable object the legacy chip-bar mutates in place
  // (mod._filterBioFilters). Seeded once; stable reference so renderFilterBar's edits accumulate.
  const [activeBio] = useState<ActiveFilters>(() => {
    const existing = getModState<ActiveFilters>(mod, "_filterBioFilters");
    if (existing) return existing;
    const fresh: ActiveFilters = {};
    setModState(mod, "_filterBioFilters", fresh);
    return fresh;
  });
  // The metric-range slider state — the SAME mutable object the island mutates in place
  // (mod._filterMetricRanges), seeded once (restored by undo/preset on a fresh mount).
  const [metricRanges] = useState<FilterMetricRanges>(() => {
    const existing = getModState<FilterMetricRanges>(mod, "_filterMetricRanges");
    if (existing) return existing;
    const fresh: FilterMetricRanges = {};
    setModState(mod, "_filterMetricRanges", fresh);
    return fresh;
  });

  const [filterVersion, setFilterVersion] = useState(0);
  const bumpVersion = useCallback(() => setFilterVersion((v) => v + 1), []);
  const activeBioSnapshot = useMemo<ActiveFilters>(() => ({ ...activeBio }), [activeBio, filterVersion]);
  // A fresh clone per version so useFilterResults (deps by reference) recomputes on a slider drag.
  const metricRangesSnapshot = useMemo<FilterMetricRanges>(() => ({ ...metricRanges }), [metricRanges, filterVersion]);

  // The reference player is a purely-internal seed for a categorical chip's default value;
  // never surfaced (renderers-filter.js:374). Snapshot: not persisted (the legacy Filter doesn't
  // store it). Live: she also picks the group (her league), so it's kept (`_filterSourcePlayer`) —
  // an Undo or a saved dashboard reopens on the same league, not whoever is sticky by then.
  const [sourcePlayer] = useState<PlayerId | null>(() => {
    const existing = LIVE ? getModState<PlayerId>(mod, "_filterSourcePlayer") : undefined;
    if (existing) return existing;
    const players = snapshotPlayersOrNone();
    const sticky = getStickyPlayer();
    const first = Object.values(players)[0];
    // Live: any player in the DB (her league is the group). Snapshot: one it has.
    const known = sticky !== null && (LIVE || playerById(players, sticky) !== undefined);
    const resolved = known ? sticky : first ? playerIdOf(first) : null;
    if (LIVE) setModState(mod, "_filterSourcePlayer", resolved ?? undefined);
    return resolved;
  });

  const [page, setPage] = useState(0);

  const slidersRowsRef = useRef<HTMLDivElement>(null);
  const islandRef = useRef<FilterSliderIsland | null>(null);

  // Live: the Men's / Women's switch (Ben, 2026-11-02): a flip lands on the other gender's top
  // league, its latest season; the sliders open at full range again (new domains, as a new season).
  const afterFlip = useCallback(() => {
    for (const k of Object.keys(metricRanges)) delete metricRanges[k];
    setSeasonPick(null);
    setModState(mod, "_filterSeason", undefined);
  }, [mod, metricRanges]);
  const gender = useGenderSwitch(mod, "_filterGender", "_filterLeagueQuiet", activeBio, bumpVersion, afterFlip);

  const board = useFilterBoard({
    womensPick: gender.pick ? womensOf(gender.pick) : null,
    leagueCleared: isLeagueCleared(mod, activeBio, "_filterLeagueQuiet"),
    sourcePlayer,
    season: seasonPick,
    mode,
    activeBio: activeBioSnapshot,
    metricRanges: metricRangesSnapshot,
    metricMap,
  });
  const { entries, season, seasonCodes, chips, players } = board;

  useEffect(() => {
    if (season) setModState(mod, "_filterSeason", season);
  }, [mod, season]);
  useEffect(() => {
    setModState(mod, "_filterValueMode", mode);
  }, [mod, mode]);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  // The players stay in view while the sliders scroll (Ben, 2026-11-02: scrolling down the stats
  // "the list of remaining players is at the top of the screen, so we lose them"). The list is
  // sticky inside the scrolling body; CSS can't size it to the body's visible height, so the body
  // hands its height over as a variable and the list scrolls on its own when it's longer.
  useEffect(() => {
    const sync = () => body.style.setProperty("--filter-body-h", `${body.clientHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(body);
    return () => ro.disconnect();
  }, [body]);

  // Any filter/season/mode change makes the current page meaningless → back to page 1, exactly
  // the legacy's renderResults() (no preservePage) on every rerender (renderers-filter.js:509).
  useEffect(() => {
    setPage(0);
  }, [season, mode, filterVersion]);

  // Build the metric-range slider column (the imperative island) into the React-owned rows host.
  // Rebuilt only on a real season/mode change (their domains rescale); a slider drag never
  // rebuilds (its onChange just bumps the version so the results recompute). FilterModule clears
  // mod._filterMetricRanges in the season/mode handlers before this runs, so a rebuilt column
  // opens at full range (renderers-filter.js:285). onChange is the stable bumpVersion.
  // `players` is the snapshot (one stable object) or, live, the group's rows — a new file, or a
  // Position chip switching to minutes-at-position rows, rescales the domains too.
  const hasGroup = board.count !== null;
  // Live: the League chip (useLeagueChip — quiet default = the canvas player's league).
  const { onChipsChange, quietKeys } = useLeagueChip(mod, activeBio, board.defaultLeague, filterVersion, bumpVersion, "_filterLeagueQuiet");
  const chipBar = (
    <FilterChipBar
      active={activeBio}
      refPlayer={chips.refPlayer}
      options={chips.options}
      rangeFilters={chips.rangeFilters}
      addMenuKeys={chips.addMenuKeys}
      newChipValue={chips.newChipValue}
      onChange={onChipsChange}
      quietKeys={quietKeys}
      optionInfo={chips.optionInfo}
    />
  );
  useEffect(() => {
    const rows = slidersRowsRef.current;
    if (!rows) return;
    islandRef.current = buildFilterSliders({
      rows,
      players,
      categories,
      seasonCode: season ?? "",
      mode,
      mod,
      onChange: bumpVersion,
    });
  }, [season, mode, categories, mod, bumpVersion, players, hasGroup]);

  // Clear every bio filter AND every metric range IN PLACE (keep the object references the
  // chip-bar + the sliders hold), redraw the sliders to full range, then bump the version so
  // the chips redraw empty + the results recompute (renderers-filter.js:557).
  const onResetAll = useCallback(() => {
    for (const k of Object.keys(activeBio)) delete activeBio[k];
    for (const k of Object.keys(metricRanges)) delete metricRanges[k];
    islandRef.current?.redrawAll();
    setFilterVersion((v) => v + 1);
  }, [activeBio, metricRanges]);

  // Per 90/Total flips every metric's real domain, so a new season's-worth of narrowed ranges
  // would mean something different on the new scale — clear them (in place) and let the slider
  // column rebuild at full range, exactly the legacy's rebuild-on-mode-change (renderers-filter.js:479).
  const onToggleMode = useCallback(() => {
    for (const k of Object.keys(metricRanges)) delete metricRanges[k];
    setMode((m) => (m === "total" ? "per90" : "total"));
    setFilterVersion((v) => v + 1);
  }, [metricRanges]);

  // A result row is a drag source (→ Bio on empty canvas, or joins a Radar/Stats), via
  // startPlayerTokenDrag, same as Squad/Rankings rows. On ?live=1 she's noted first (name +
  // gender): a live Stats tile takes her only once her gender is known (T2-14b).
  const onRowPointerDown = (e: ReactPointerEvent<HTMLDivElement>, entry: ProtoFilterResult) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    if (LIVE) noteRowPlayer({ id: entry.playerId, name: entry.name, team: entry.team }, board.womens);
    setStickyPlayer(entry.playerId);
    startPlayerTokenDrag(e.nativeEvent);
  };

  const seasonItems = useMemo<StyledSelectItem[]>(
    () => seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) })),
    [seasonCodes],
  );

  const anyActive =
    Object.keys(activeBioSnapshot).some((k) => activeBioSnapshot[k] !== undefined) ||
    Object.keys(metricRangesSnapshot).length > 0;
  const totalPages = Math.max(1, Math.ceil(entries.length / RESULTS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * RESULTS_PAGE_SIZE;
  const pageItems = entries.slice(start, start + RESULTS_PAGE_SIZE);
  // Live: a player listed once per position says why on hover (Q-014). Empty on the snapshot site.
  const twice = useMemo(() => whyTwice(entries as PositionRow[]), [entries]);

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainFilter({
            season: humanizeSeasonCode(season ?? ""),
            mode: mode === "total" ? "total" : "per90",
            filters: activeBioSnapshot,
            refName: shortNameOrPlayer(sourcePlayer),
            count: board.count,
            label: board.label ?? null,
            sliders: Object.keys(metricRangesSnapshot).map((k) => metricMap.get(k)?.name ?? k),
            switchedTo: board.gender && board.gender.shown !== board.gender.player ? board.gender.shown : null,
          })}
        />
      )}
      <SubControls mod={mod} body={body}>
        <StyledSelect
          items={seasonItems}
          value={season ?? ""}
          onChange={(next) => {
            // A new season has new real domains → clear narrowed ranges (in place) so the
            // rebuilt sliders open at full range (renderers-filter.js:357).
            for (const k of Object.keys(metricRanges)) delete metricRanges[k];
            setSeasonPick(next);
            setModState(mod, "_filterSeason", next);
            setFilterVersion((v) => v + 1);
            body.scrollTop = 0;
          }}
          wrapClass="filter-season-select-wrap"
          extraClass="filter-season-select"
        />
        {LIVE && board.gender && (
          <GenderSwitch womens={board.gender.shown} onFlip={() => gender.flip(board.gender!.shown, board.gender!.player)} />
        )}
      </SubControls>

      <PopulationReadout
        mod={mod}
        body={body}
        count={board.count}
        showToggle={anyTotalMode}
        mode={mode}
        onToggle={onToggleMode}
        label={board.label ?? "players match"}
      />

      {/* Live with no group (a league's season isn't built yet): the readout says why, and the
          chips stay so another league can be picked (Ben, 2026-10-16). */}
      {!hasGroup && LIVE && <div className="filter-bar">{chipBar}</div>}
      {hasGroup && (
        <div className="filter-wrap">
          {/* .filter-bar: the shared React FilterChipBar (chips + "+ Add filter"), then
              Reset-all. No more legacy chip-bar island. */}
          <div className="filter-bar">
            {chipBar}
            <button
              className="add-filter-btn filter-reset-all-btn"
              style={{ display: anyActive ? "" : "none" }}
              onClick={onResetAll}
            >
              Reset all
            </button>
          </div>

          <div className="filter-body-layout">
            {/* The metric-range slider column (04g-4c) — an imperative island: React owns this
                host but renders it childless; buildFilterSliders fills + wires it. */}
            <div className="filter-sliders-pane">
              <div className="filter-sliders-rows" ref={slidersRowsRef}></div>
            </div>
            <div className="filter-results-pane">
              {entries.length === 0 ? (
                <div className="empty-note">No players match. Widen a slider or remove a filter.</div>
              ) : (
                <>
                  <div className="filter-results-list">
                    {pageItems.map((e) => (
                      <div
                        key={`${e.playerId}:${e.competition ?? ""}`}
                        className="ranking-row"
                        onPointerDown={(ev) => onRowPointerDown(ev, e)}
                        data-tooltip={twice.get(positionRowKey(e))}
                      >
                        <span className="ranking-player">
                          <span className="ranking-player-name">{e.shortName}</span>
                          <span className="ranking-player-meta">
                            {/* The club alone (Ben, 2026-10-31: "Brentford will suffice"); the league only when there's no club. */}
                            {e.team || e.competition}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="filter-results-pager">
                      <button
                        className="filter-page-btn filter-page-prev"
                        aria-label="Previous page"
                        disabled={safePage === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                      >
                        ‹
                      </button>
                      <span className="filter-page-readout">
                        {(start + 1).toLocaleString()}–
                        {Math.min(start + RESULTS_PAGE_SIZE, entries.length).toLocaleString()} of{" "}
                        {entries.length.toLocaleString()}
                      </span>
                      <button
                        className="filter-page-btn filter-page-next"
                        aria-label="Next page"
                        disabled={safePage >= totalPages - 1}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
